
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, Search, FileText, BarChart2, ArrowUpRight, PieChart, LineChart, TrendingUp, Settings, ArrowRight, Target } from "lucide-react";
import CallToAction from "@/components/CallToAction";

const CompetitiveKeywords = () => {
  return (
    <PageTransition>
      <Helmet>
        <title>Medium Competitive SEO Services | Los Angeles SEO Group</title>
        <meta name="description" content="Rank for medium-competitive keywords with our balanced SEO approach. Content creation, on-page SEO, and link building for businesses in moderately competitive markets." />
        <link rel="canonical" href="https://losangelesseogroup.com/services/competitive-keywords" />
      </Helmet>

      <Navbar />

      <main className="flex-grow pt-28 pb-16">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-background to-muted/30 py-16">
          <div className="page-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1.5 mb-6 text-sm font-medium rounded-full bg-desert-sunset/10 text-desert-sunset">
                  Medium Competitive Keywords
                </span>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-6">
                  Win the <span className="text-gradient bg-desert-gradient">Ranking Battle</span> for Valuable Keywords
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  Balanced approach for businesses in moderately competitive markets seeking sustained growth through targeted keyword strategies.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" className="bg-desert-gradient hover:opacity-90">
                    <Link to="/contact" className="flex items-center">
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/case-studies">
                      See Success Stories
                    </Link>
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-desert-gradient opacity-10 blur-xl rounded-full"></div>
                <div className="relative bg-card border border-border shadow-lg rounded-xl p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center">
                      <div className="bg-desert-sunset/10 p-2 rounded-lg mr-3">
                        <Search className="h-6 w-6 text-desert-sunset" />
                      </div>
                      <div>
                        <h3 className="font-bold">Keyword Performance</h3>
                        <div className="text-xs text-muted-foreground">Last 90 days</div>
                      </div>
                    </div>
                    <div className="bg-emerald-500/10 px-2 py-1 rounded text-emerald-500 text-xs font-medium flex items-center">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      +47%
                    </div>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {[
                      { keyword: "orthopedic surgeons los angeles", position: 3, change: "+12" },
                      { keyword: "best knee surgeon near me", position: 1, change: "+4" },
                      { keyword: "sports medicine specialist", position: 5, change: "+16" },
                      { keyword: "joint replacement surgery", position: 2, change: "+8" },
                      { keyword: "arthroscopic surgery cost", position: 4, change: "+7" }
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm p-2 rounded hover:bg-muted/50 transition-colors">
                        <div className="flex items-center">
                          <div className="w-5 h-5 rounded-full bg-desert-sunset/10 text-desert-sunset flex items-center justify-center text-xs mr-2">
                            {item.position}
                          </div>
                          <span>{item.keyword}</span>
                        </div>
                        <div className="text-emerald-500 font-medium">
                          {item.change}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-muted/50 p-3 rounded-lg text-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">Organic Traffic</span>
                      <div className="text-desert-sunset font-medium">18,452</div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-desert-gradient rounded-full" style={{ width: '67%' }}></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Comprehensive <span className="text-gradient bg-desert-gradient">SEO Strategies</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Our medium-competitive keyword SEO services include everything you need to outrank your competition
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Target className="h-6 w-6 text-desert-sunset" />,
                  title: "Competitive Keyword Targeting",
                  description: "Strategic focus on medium-difficulty keywords with high conversion potential and achievable rankings."
                },
                {
                  icon: <FileText className="h-6 w-6 text-desert-sunset" />,
                  title: "Content Creation & Optimization",
                  description: "High-quality, SEO-optimized content that satisfies search intent and engages your audience."
                },
                {
                  icon: <Settings className="h-6 w-6 text-desert-sunset" />,
                  title: "On-page SEO Implementation",
                  description: "Technical optimization of all page elements to maximize relevance and searchability."
                },
                {
                  icon: <LineChart className="h-6 w-6 text-desert-sunset" />,
                  title: "Technical SEO Audit & Fixes",
                  description: "Comprehensive analysis and resolution of technical issues affecting crawling and indexing."
                },
                {
                  icon: <ArrowUpRight className="h-6 w-6 text-desert-sunset" />,
                  title: "Link Building Strategy",
                  description: "Earning quality backlinks from relevant sources to boost domain authority and rankings."
                },
                {
                  icon: <PieChart className="h-6 w-6 text-desert-sunset" />,
                  title: "Performance Tracking Dashboard",
                  description: "Real-time monitoring of rankings, traffic, and conversion metrics for all target keywords."
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card border border-border rounded-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="bg-desert-sunset/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 bg-muted/30">
          <div className="page-container">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold mb-6">
                    Why Choose Our <span className="text-gradient bg-desert-gradient">Medium-Competitive</span> SEO
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    We specialize in the sweet spot of SEO - targeting valuable keywords that have reasonable competition levels for the best ROI.
                  </p>
                  
                  <ul className="space-y-4">
                    {[
                      "Balanced approach to maximize results within budget",
                      "Focus on keywords with high conversion potential",
                      "Content strategy aligned with business goals",
                      "Sustainable, white-hat tactics only",
                      "Regular strategy calls to adjust approach",
                      "Transparent reporting on all metrics"
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 * index }}
                        className="flex items-start"
                      >
                        <CheckCircle className="h-5 w-5 text-desert-sunset mr-2 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-xl overflow-hidden shadow-lg"
                >
                  <div className="bg-desert-gradient p-6 text-white">
                    <h3 className="text-xl font-bold mb-2">Medium Competitive Keywords</h3>
                    <div className="flex items-baseline mb-4">
                      <span className="text-3xl font-bold">$2,000</span>
                      <span className="text-white/80 ml-2">/ month</span>
                    </div>
                    <p className="text-white/80 text-sm">
                      Balanced approach for businesses in moderately competitive markets seeking growth.
                    </p>
                  </div>
                  
                  <div className="p-6">
                    <ul className="space-y-3 mb-6">
                      {[
                        "Medium competition keyword targeting",
                        "Content creation and optimization",
                        "On-page SEO implementation",
                        "Technical SEO audit and fixes",
                        "Link building strategy",
                        "Performance tracking dashboard",
                        "Bi-weekly strategy calls"
                      ].map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-desert-sunset mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button asChild className="w-full bg-desert-gradient hover:opacity-90">
                      <Link to="/contact">Start Your Campaign</Link>
                    </Button>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16">
          <div className="page-container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                <span className="text-gradient bg-desert-gradient">Results You</span> Can Expect
              </h2>
              <p className="text-lg text-muted-foreground">
                Our medium-competitive keyword SEO services deliver measurable improvements in rankings, traffic, and conversions
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  metric: "Keyword Rankings",
                  value: "Page 1",
                  description: "For target keywords within 3-6 months"
                },
                {
                  metric: "Organic Traffic",
                  value: "+125%",
                  description: "Average traffic increase within 6 months"
                },
                {
                  metric: "Conversion Rate",
                  value: "+45%",
                  description: "Typical improvement from qualified traffic"
                }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="glassmorphism dark:glassmorphism-dark rounded-xl p-6 text-center"
                >
                  <h3 className="text-lg text-muted-foreground mb-2">{stat.metric}</h3>
                  <div className="text-3xl md:text-4xl font-bold text-gradient bg-desert-gradient mb-2">
                    {stat.value}
                  </div>
                  <p className="text-sm text-muted-foreground">{stat.description}</p>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <Button asChild variant="outline" size="lg">
                <Link to="/case-studies" className="flex items-center">
                  View Detailed Case Studies
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CallToAction className="max-w-5xl mx-auto mb-16" />
      </main>

      <Footer />
    </PageTransition>
  );
};

export default CompetitiveKeywords;
